import { useQuery } from '@vue/apollo-composable';
import { refDebounced } from '@vueuse/core';
import { computed, ref, watch } from 'vue';

import type { GetSwitchableOrganizationsQuery } from '@/entities/organization';

import { useAuthStore } from '@/entities/auth';
import { OrganizationSwitchAccess } from '@/entities/organization';
import { useImpersonation } from '@/features/impersonation';
import { useConfirmLeavingKassa, useLocationSelection } from '@/features/location-switch';
import { useApolloQueryWithPreviousData, useCursorLoadMore } from '@/shared/api';
import { GetSwitchableOrganizationsDocument } from '@shared/api/graphql/gql/graphql';

const PAGE_SIZE = 20;
/** Поиск нужен, когда организаций без фильтра (вместе с текущей) не меньше этого числа. */
const SEARCH_THRESHOLD = 8;

export type SwitchableOrganization =
  GetSwitchableOrganizationsQuery['switchableOrganizations']['edges'][number]['node'];

/**
 * Блок «Организация» в плашке: список с сервера порциями, поиск и маршрутизация клика.
 * Суперюзер видит все организации и входит в чужие под владельцем; во время
 * impersonation в свою организацию он возвращается через выход из impersonation.
 */
export function useOrganizationSwitcher() {
  const auth = useAuthStore();
  const impersonation = useImpersonation();
  const { selectOrganization } = useLocationSelection();
  const { confirmLeavingKassa } = useConfirmLeavingKassa();

  const open = ref(false);
  const search = ref('');
  const debouncedSearch = refDebounced(search, 300);
  const normalizedSearch = computed(() => debouncedSearch.value.trim());

  const { result, loading, fetchMore } = useQuery(
    GetSwitchableOrganizationsDocument,
    () => ({ search: normalizedSearch.value || null, first: PAGE_SIZE, after: null }),
    () => ({ enabled: open.value }),
  );
  const { displayData } = useApolloQueryWithPreviousData(result, loading);
  const pageInfo = computed(() => displayData.value?.switchableOrganizations.pageInfo);
  const { loadMore } = useCursorLoadMore(pageInfo, fetchMore);

  /** Сбой догрузки останавливает прокрутку до смены поиска, иначе она повторяется бесконечно. */
  const loadMoreFailed = ref(false);
  watch(normalizedSearch, () => {
    loadMoreFailed.value = false;
  });
  const canLoadMore = computed(() => pageInfo.value?.hasNextPage === true && !loadMoreFailed.value);
  const loadNextPage = async (): Promise<void> => {
    try {
      await loadMore();
    } catch {
      loadMoreFailed.value = true;
    }
  };

  const currentOrganizationId = computed(() => auth.currentOrganization?.id ?? auth.impersonation.organization?.id);
  const organizations = computed(() =>
    (displayData.value?.switchableOrganizations.edges ?? [])
      .map((edge) => edge.node)
      .filter((organization) => organization.id !== currentOrganizationId.value),
  );

  // Порог берётся из ответа без фильтра и запоминается: поле не пропадает при вводе.
  const reachedSearchThreshold = ref(false);
  watch(result, (value) => {
    if (value && normalizedSearch.value === '' && value.switchableOrganizations.pageInfo.total >= SEARCH_THRESHOLD) {
      reachedSearchThreshold.value = true;
    }
  });
  const showSearch = computed(() => search.value !== '' || reachedSearchThreshold.value);

  const visible = computed(
    () => auth.actsAsSuperUser || (auth.availableOrganizations.length > 1 && !auth.isImpersonating),
  );

  const select = async (organization: SwitchableOrganization): Promise<void> => {
    if (organization.access === OrganizationSwitchAccess.NoOwner) {
      return;
    }

    if (organization.access === OrganizationSwitchAccess.Member && !auth.isImpersonating) {
      await selectOrganization(organization.id);
      return;
    }

    if (
      !(await confirmLeavingKassa({ organizationId: organization.id, locationId: null, title: organization.title }))
    ) {
      return;
    }

    if (organization.access === OrganizationSwitchAccess.Member) {
      await impersonation.stop({ organizationId: organization.id });
    } else {
      await impersonation.enterOrganization(organization.id);
    }
  };

  return {
    open,
    search,
    organizations,
    isLoading: loading,
    canLoadMore,
    loadNextPage,
    showSearch,
    visible,
    select,
  };
}
